// EngagementsDetails.tsx
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAddEngagementActionMutation,
  useChangeEngagementStatusMutation,
  useGetEngagmentsUserQuery,
  useUpdateEngagementDetailsMutation,
} from '../../../../reduxToolkit/reducers/usersSlice';
import {
  Col,
  Card,
  CardBody,
  Container,
  Row,
  Button,
  CardHeader,
  CardFooter,
  Label,
  Input,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  UncontrolledTooltip,
} from 'reactstrap';
import Loader from '../../../../layout/Loader';
import Breadcrumbs from '../../../../coreComponents/breadcrumbs';
import CommonCardHeader from '../../../../coreComponents/CommonCardHeader';
import SimpleModal from '../../../uiKits/modal/basicModal/SimpleModal';
import { useCallback, useEffect, useRef, useState } from 'react';
import TimePickerWithLimitedTime from '../../../formsAndTable/forms/formWidgets/datePicker/timePicker/TimePickerWithLimitedTime';
import CommentFileModal, { PreviewItem } from '../../../commetFileModal';
import { validateImages } from '../../../../lib/filesUtils';
import ActionsStage from './ActionsStage';
import {
  EngagementsCancelReasons,
  EngagenemtStatus,
  TaskType,
} from '../../../../types/Engagements';
import { sleep } from '../../[id]/LeadsDetails';
import Swal from 'sweetalert2';
import Error404 from '../../../pages/others/errorPage/Error404';
import { routes } from '../../../../routers/routes';
import { isNewPreview } from '../../../../utils';
import { getCancelEngagementReasons } from '../../../../services/api';
import { formatWithOffset } from '../../../../lib/timeUtils';
import { baseLang } from '../../../../lib/utils';

const EngagementsDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const pageLang = baseLang(i18n.language);
  const {
    data: engagementUserData,
    isSuccess,
    isLoading,
    isError,
  } = useGetEngagmentsUserQuery(id!);

  const [breadcrumbs] = useState<{ name: string; link: string }[]>([]);
  const [previews, setPreviews] = useState<PreviewItem[]>([]);
  const [comment, setComment] = useState('');
  const [showCreateAction, setShowCreateAction] = useState(false);
  const [openLeadDetails, setLeadDetails] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false);
  const [closeReason, setCloseReason] = useState<{
    id: number;
    statusName: string;
    reason: string;
    colorCode: string;
    type: string | null;
    isSystem: boolean;
  } | null>(null);
  const [isCloseCommentRequired, setIsCloseCommentRequired] = useState(false);
  const [dropDownOptions, setDropDownOptions] = useState<any[]>([]);
  const [cancelReasonsRaw, setCancelReasonsRaw] = useState<any[] | null>(null);
  const [closeReasonRawId, setCloseReasonRawId] = useState<number | null>(null);

  useEffect(() => {
    if (!cancelReasonsRaw) return;
    const translated = cancelReasonsRaw.map((item) => ({
      id: item.id,
      colorCode: item.colorCode,
      statusName: t(`global.closeReason.${item.reason}`),
      reason: item.reason,
      type: item.type,
      isSystem: true,
    }));
    setDropDownOptions(translated);
    if (closeReasonRawId != null) {
      const sel = translated.find((opt) => opt.id === closeReasonRawId) || null;
      setCloseReason(sel);
    }
  }, [cancelReasonsRaw, pageLang, closeReasonRawId, t]);
  const [updateEnagedStatus] = useChangeEngagementStatusMutation();
  const [updateEngadedDetailsData] = useUpdateEngagementDetailsMutation();
  const [addEngagementAction] = useAddEngagementActionMutation();

  const [newAction, setNewAction] = useState<{
    id?: number;
    taskType: TaskType | null;
    title: string;
    deadline: Date | null;
  }>({
    id: Number(id),
    taskType: null,
    title: '',
    deadline: null,
  });

  const navigate = useNavigate();

  const makeAbs = useCallback(
    (rel?: string) =>
      rel ? `${process.env.REACT_APP_BACKEND_STATIC}/crm/${rel}` : 'data:image/gif;base64,R0lG...',
    [],
  );
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (engagementUserData) {
      (async () => {
        const data = await getCancelEngagementReasons();
        if (Array.isArray(data)) {
          // store raw data and translate later in a language-aware effect
          setCancelReasonsRaw(data);
        }
        const preselected = (data as any[]).find((option) => option.reason === 'other');
        setCloseReasonRawId(preselected?.id ?? null);
        setIsCloseCommentRequired(true);
      })();
    }
  }, [isSuccess, engagementUserData]);

  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        if (isNewPreview(p)) URL.revokeObjectURL(p.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilesSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const { valid } = validateImages(files, t);
    if (valid.length) {
      const newPreviews: PreviewItem[] = valid.map((file) => ({
        kind: 'new',
        file,
        url: URL.createObjectURL(file),
      }));
      setPreviews((prev) => [...prev, ...newPreviews]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpenCreateActionModal = (id: string) => {
    setNewAction({
      id: Number(id),
      taskType: null,
      title: '',
      deadline: null,
    });
    setShowCreateAction(true);
  };

  const taskTypesSelects = [
    { id: 1, value: TaskType.CALL, title: t('leads.addActionForm.selectCall') },
    { id: 2, value: TaskType.EMAIL, title: t('leads.addActionForm.selectEmail') },
    { id: 3, value: TaskType.OTHER, title: t('leads.addActionForm.selectOther') },
  ];

  const abortNewAction = () => {
    setNewAction({ id: Number(id), taskType: null, title: '', deadline: null });
    setShowCreateAction(false);
  };

  const handleCreateNewAction = async () => {
    console.log('newAction', newAction);
    if (!newAction?.deadline || !newAction.title?.trim() || !newAction.taskType) {
      setShowCreateAction(false);
      return;
    }
    try {
      const res = await addEngagementAction(newAction).unwrap();
      setShowCreateAction(false);
      await sleep(500);
      if (!res.success && !res?.code) {
        Swal.fire({
          icon: 'error',
          title: t('leads.notifications.errorTitle'),
          text: t('leads.notifications.errorText'),
        });
      }
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: t('leads.notifications.successTitle'),
          text: t('leads.notifications.successText'),
        });
      }
    } catch (error) {
      console.log('error', error);
    }
  };
  const handleChandeEngagementStatus = async (status: EngagenemtStatus) => {
    try {
      await updateEnagedStatus({
        userId: engagementUserData!.user_id,
        status,
      }).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const toggleLeadDetails = useCallback((id: string) => {
    setLeadDetails((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const reasonChangeHandler = (value: any) => {
    const selectedOption = dropDownOptions.find((option) => option.id === value.id);
    if (
      selectedOption &&
      (selectedOption.reason === EngagementsCancelReasons.OTHER ||
        selectedOption.reason === EngagementsCancelReasons.REFUSED_FOREVER ||
        selectedOption.reason === EngagementsCancelReasons.REFUSED_TEMPORARILY)
    ) {
      setIsCloseCommentRequired(true);
    } else {
      setIsCloseCommentRequired(false);
    }
    setCloseReason(selectedOption);
  };

  const handleCommentFileModalClose = () => {
    setShowDeclineConfirmation(false);
    setComment('');
    const initialReason =
      dropDownOptions.find((option) => option.reason === EngagementsCancelReasons.OTHER) || null;
    setCloseReason({
      id: initialReason?.id,
      colorCode: initialReason?.colorCode,
      statusName: initialReason ? t(`global.closeReason.${initialReason.reason}`) : '',
      reason: initialReason?.reason || '',
      type: initialReason?.type || null,
      isSystem: true,
    });
    setIsCloseCommentRequired(true);
  };

  const handleSubmitStatusChange = async () => {
    const payload = {
      userId: engagementUserData!.user.id,
      status: EngagenemtStatus.CLOSED,
      cancelReason: closeReason,
      cancelComment: comment,
    };
    console.log('Submitting status change with payload:', payload);
    try {
      await updateEnagedStatus(payload).unwrap();
      handleCommentFileModalClose();
      await sleep(500);

      Swal.fire({
        icon: 'success',
        title: t('leads.notifications.successTitle'),
        text: t('leads.notifications.successText'),
      });
    } catch (error) {
      console.log('error', error);
      Swal.fire({
        icon: 'error',
        title: t('leads.notifications.errorTitle'),
        text: t('leads.notifications.errorText'),
      });
    }
  };

  if (isLoading) return <Loader />;
  if (isError || !engagementUserData?.success)
    return (
      <Error404
        goto={routes.Leads.Engagement}
        buttonLabel={t('global.backButtonLabel.engagements')}
      />
    );

  return (
    <>
      <Breadcrumbs mainTitle={t('engagements.userDetails')} parentsArray={breadcrumbs} />
      <Container fluid>
        <Row>
          <Card>
            <CardBody>
              <Col sm="12" className="d-flex">
                <Col sm="6">
                  <Row>
                    <Col sm="3">
                      <p>{t('leads.table.name')}:</p>
                    </Col>
                    <Col sm="9">
                      <p>
                        {engagementUserData?.user.firstName} {engagementUserData?.user.lastName}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="3">
                      <p>Email:</p>
                    </Col>
                    <Col sm="9">
                      <p>{engagementUserData?.user.email}</p>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="3">
                      <p>{t('leads.table.phone')}:</p>
                    </Col>
                    <Col sm="9">
                      <p>{engagementUserData?.user.telegram}</p>
                    </Col>
                  </Row>
                </Col>

                <Col sm="6">
                  <Row className="d-flex justify-content-between position-relative">
                    <Row>
                      <Col sm="12" className="d-flex gap-1">
                        <p className="p-0 m-0">{t('leads.status.title')}:</p>
                        <p className="p-0 m-0">
                          {t(`engagements.userStatus.${engagementUserData?.status}`)}
                        </p>
                      </Col>
                    </Row>
                    <Col sm="12" className="mt-2">
                      <Row className="g-2 align-items-center w-100">
                        {engagementUserData.closed_at && engagementUserData.cancel_reason && (
                          <Col xs="auto" className="m-0">
                            <div className="d-flex flex-column">
                              <p className="m-0">
                                {t('leads.cancelReason.title')}
                                {t(`global.closeReason.${engagementUserData?.cancel_reason}`)}
                              </p>
                              <p className="m-0" id={`lead-${engagementUserData?.user_id}-comment`}>
                                {t('leads.cancelReason.comment')}
                                {engagementUserData?.cancel_comment &&
                                engagementUserData.cancel_comment.length > 30
                                  ? `${engagementUserData.cancel_comment.substring(0, 30)}...`
                                  : engagementUserData?.cancel_comment}
                              </p>
                              {engagementUserData?.cancel_comment &&
                                engagementUserData.cancel_comment.length > 30 && (
                                  <UncontrolledTooltip
                                    target={`lead-${engagementUserData?.user_id}-comment`}
                                    placement="bottom-start"
                                    container="body"
                                    delay={{ show: 50, hide: 50 }}
                                    style={{
                                      minWidth: '200px',
                                      backgroundColor: 'var(--balance-profie-bg)',
                                      textAlign: 'left',
                                    }}
                                  >
                                    {engagementUserData?.cancel_comment}
                                  </UncontrolledTooltip>
                                )}
                              <p className="m-0">
                                {t('leads.cancelReason.date', {
                                  date: formatWithOffset(
                                    engagementUserData?.closed_at,
                                    timezone,
                                    undefined,
                                  ),
                                })}
                              </p>
                            </div>
                          </Col>
                        )}

                        <Col xs="auto" className="mt-0 p-0 ms-auto me-0">
                          <Button
                            color="primary"
                            outline
                            className="hover:text-black"
                            onClick={() =>
                              navigate(`/dashboard/stat-details/${engagementUserData?.user_id}`)
                            }
                          >
                            {t('stats.userStats')}
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Col>
              <Row>
                <Col sm="6" className="pt-2">
                  <Accordion
                    open={openLeadDetails}
                    toggle={toggleLeadDetails}
                    className="dark-accordion"
                    flush={true}
                  >
                    <AccordionItem key={1}>
                      <AccordionHeader
                        className="bg-light rounded border-secondary"
                        targetId={String(engagementUserData?.id)}
                      >
                        <span className="text-muted">{t('leads.metadata.title')}</span>
                      </AccordionHeader>
                      <AccordionBody accordionId={String(engagementUserData?.id)}>
                        <Col sm="12">
                          <Card>
                            {engagementUserData?.sourceMeta &&
                              Object.keys(engagementUserData.sourceMeta).length > 0 && (
                                <CommonCardHeader title={t('engagements.metadata.header')} tag />
                              )}

                            <CardBody>
                              <Row className="mb-2">
                                <Col sm="12">
                                  {engagementUserData?.sourceMeta &&
                                  Object.keys(engagementUserData?.sourceMeta).length > 0 ? (
                                    Object.entries(engagementUserData?.sourceMeta).map(
                                      ([key, value]) => (
                                        <Row key={key} className="mb-1">
                                          <Col sm="3">
                                            <strong>{key}:</strong>
                                          </Col>
                                          <Col sm="9">{String(value)}</Col>
                                        </Row>
                                      ),
                                    )
                                  ) : (
                                    <span>{t('leads.metadata.noMeta')}</span>
                                  )}
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>
                        </Col>
                      </AccordionBody>
                    </AccordionItem>
                  </Accordion>
                </Col>
                <Col sm="6" className="pt-2 d-flex align-items-center">
                  <Button
                    color="danger"
                    onClick={(e) => {
                      e.preventDefault();
                      // handleChandeEngagementStatus(EngagenemtStatus.CLOSED);
                      setShowDeclineConfirmation(true);
                    }}
                    disabled={engagementUserData?.status === EngagenemtStatus.CLOSED}
                  >
                    {t('engagements.closeCase')}
                  </Button>
                </Col>
              </Row>
              {engagementUserData?.flows?.map((flowDataItem) => (
                <ActionsStage
                  key={flowDataItem.id}
                  id={engagementUserData.id}
                  engagement={engagementUserData}
                  flowDataItem={flowDataItem}
                  handleOpenCreateActionModal={handleOpenCreateActionModal}
                  userStatus={engagementUserData.status}
                  stageStatus={flowDataItem.stageStatus}
                  updateEngadedDetailsData={updateEngadedDetailsData}
                />
              ))}{' '}
            </CardBody>
          </Card>{' '}
        </Row>
      </Container>

      <SimpleModal isOpen={showCreateAction} close={() => setShowCreateAction(false)}>
        <Card>
          <CardHeader>
            <CommonCardHeader title={t('leads.addActionForm.mainTitle')} tagClass="text-unclamp" />
          </CardHeader>
          <CardBody>
            <Label htmlFor="actionTitle">{t('leads.addActionForm.label')}</Label>
            <Input
              type="text"
              name="actionTitle"
              value={newAction?.title}
              onChange={(e) => setNewAction({ ...newAction, title: e.target.value })}
            />

            <Label className="mt-3 d-flex flex-column">
              {t('leads.addActionForm.labelTaskType')}
            </Label>
            <Input
              type="select"
              required
              name="taskTypeStatus"
              value={newAction.taskType ?? ''}
              onChange={(e) => setNewAction({ ...newAction, taskType: e.target.value as TaskType })}
            >
              <option value="" disabled hidden>
                {t('leads.addActionForm.selectPlaceholder')}
              </option>
              {taskTypesSelects.map((tt) => (
                <option key={tt.id} value={tt.value}>
                  {tt.title}
                </option>
              ))}
            </Input>

            <Label className="mt-3">{t('leads.addActionForm.setDeadline')}</Label>
            <TimePickerWithLimitedTime
              setDateTime={(date) => setNewAction((prev) => ({ ...prev, deadline: date }))}
              start={0}
              disabledPeriods={[]}
              initialSelection={null}
              preferExactSelection
              workingHours={{ start: '07:00', end: '22:00' }}
            />
          </CardBody>
          <CardFooter>
            <Row className="g-2">
              <Col xs="auto">
                <Button color="primary" onClick={handleCreateNewAction}>
                  {t('global.ok')}
                </Button>
              </Col>
              <Col xs="auto">
                <Button onClick={abortNewAction}>{t('global.cancel')}</Button>
              </Col>
            </Row>
          </CardFooter>
        </Card>
      </SimpleModal>
      <CommentFileModal
        isOpen={showDeclineConfirmation}
        onClose={handleCommentFileModalClose}
        labels={{
          title: t('leads.commentForm.label'),
          placeholder: t('leads.commentForm.placeholder'),
          ok: t('global.ok'),
          saving: t('global.saving'),
          cancel: t('global.cancel'),
          remove: t('common.remove'),
          selectStatus: t('global.selector.selectStatus'),
        }}
        commentRef={commentRef as React.RefObject<HTMLTextAreaElement>}
        commentRequired={isCloseCommentRequired}
        comment={comment}
        onCommentChange={setComment}
        onSubmit={handleSubmitStatusChange}
        dropDown={true}
        hasFileInput={false}
        dropDownContent={dropDownOptions}
        dropDownPreSelected={closeReason}
        dropDownSelectHandler={reasonChangeHandler}
        filesRequired={false}
        isClearable={false}
      />
    </>
  );
};

export default EngagementsDetails;
